import React from "react";
import pRetry, { AbortError } from "p-retry";
import {
  ApiResponse,
  SocketOps,
  SocketTopics,
  WebSocketResponse,
  WorkspaceType,
} from "@shared/types";
import { SocketArgs, TopicReducerHandler } from "@shared/services/socket/types";
import { refreshAndPersistToken } from "@shared/services/baseQuery";
import { v4 as uuidv4 } from "uuid";
import { sharedLib } from "@shared/services/shared.service";
import { socketsSlices } from "@shared/slices/sockets.slice";
import { topicReducer } from "@shared/services/socket/constants";
import { IS_NET_CONNECTED } from "@shared/common/constants";
import moment, { Moment } from "moment";
import { Mutex, MutexInterface, tryAcquire, withTimeout } from "async-mutex";
import { getStoreWorkspace } from "@shared/common/utils";

class SocketHandler extends React.Component<{ url: string }> {
  private socket: WebSocket | null = null;
  private tabId: string = uuidv4();
  private subscribedTopics: Set<SocketTopics> = new Set();
  private connectionMutex: MutexInterface = new Mutex();
  private subscriptionsMutex: MutexInterface = withTimeout(new Mutex(), 10000);
  private pendingPromises: WaitingResponseData = {};
  private pendingSubscriptionsActions: {
    [key in SocketTopics]?: SubscriptionData;
  } = {};
  private initializedIntervals = false;
  private initialized = false;
  private pingProcessing = false;

  initialize = () => {
    this.connectionMutex
      .runExclusive(async () => {
        return this.connect();
      })
      .catch(() => {})
      .finally(() => {
        this.initialized = true;
        if (this.initializedIntervals) return;
        this.sendPings();
        this.processSubscriptionActions();
        this.initializedIntervals = true;
      });
  };

  private getSocketUrl = () => {
    const workspace = getStoreWorkspace();
    const baseUrl = this.props.url;
    if (workspace === WorkspaceType.DEMO)
      return baseUrl.replace("/ws/ws/", "/demo-ws/ws/");
    return baseUrl;
  };

  private handleSubscriptionAction = (
    topic: SocketTopics,
    subscriptionData: SubscriptionData
  ) => {
    const { actionType, numRetries } = subscriptionData;
    let promise:
      | Promise<void | ApiResponse<WebSocketResponse<unknown>> | undefined>
      | undefined;
    if (
      actionType === SubscriptionAction.UNSUBSCRIBE &&
      this.subscribedTopics.has(topic)
    ) {
      promise = this.sendUnsubscribeMsg(topic);
    } else if (actionType === SubscriptionAction.SUBSCRIBE) {
      promise = this.sendSubscribeMsg([topic]);
    } else if (actionType === SubscriptionAction.RE_SUBSCRIBE) {
      promise = this.resubscribeToTopic(topic);
    }
    return promise
      ?.then(() => {
        delete this.pendingSubscriptionsActions[topic];
        if (actionType === SubscriptionAction.UNSUBSCRIBE) {
          this.subscribedTopics.delete(topic);
        } else {
          this.subscribedTopics.add(topic);
        }
      })
      .catch(() => {
        if (numRetries === 1) {
          delete this.pendingSubscriptionsActions[topic];
        } else {
          subscriptionData.numRetries += 1;
        }
      });
  };

  private processSubscriptionActions = () => {
    setInterval(async () => {
      if (!this.initialized) return;
      try {
        await tryAcquire(this.subscriptionsMutex)
          .runExclusive(async () => {
            if (!IS_NET_CONNECTED || !this.isConnected()) return;
            const promises = Object.entries(
              this.pendingSubscriptionsActions
            ).map(([topic, subscriptionData]) => {
              return this.handleSubscriptionAction(
                topic as SocketTopics,
                subscriptionData
              );
            });
            await Promise.allSettled(promises);
          })
          .catch(() => {});
      } catch {}
    }, 1000);
  };

  private sendPings = () => {
    setInterval(() => {
      if (!this.initialized || this.pingProcessing || !IS_NET_CONNECTED) return;
      this.pingProcessing = true;
      this.sendSocketMessage({ op: SocketOps.PING })
        .catch(async () => {
          return this.reconnect();
        })
        .catch(() => {})
        .finally(() => {
          this.pingProcessing = false;
        });
    }, 10000);
  };

  private waitForResponseData = (
    sendData: (uuid: string) => void
  ): Promise<ApiResponse<WebSocketResponse<unknown>> | undefined> => {
    const uuid = uuidv4();
    return new Promise((resolve, reject) => {
      this.pendingPromises[uuid] = {
        resolve,
        reject,
        requestTime: moment(),
      };
      setTimeout(() => {
        delete this.pendingPromises[uuid];
        reject(new Error("Timeout"));
      }, 5000);
      if (this.isConnected()) {
        sendData(uuid);
      } else {
        delete this.pendingPromises[uuid];
        reject(new Error("Not connected!"));
      }
    });
  };

  private waitForFlag = async (
    getFlagValue: () => boolean,
    timeoutSeconds = 100,
    intervalMs = 100
  ) => {
    return new Promise(async (resolve, reject) => {
      let loopNum = 0;
      const timeoutMs = timeoutSeconds * 1000;
      const maxLoop = timeoutMs / intervalMs;
      while (!getFlagValue() && loopNum < maxLoop) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
        loopNum++;
      }
      if (getFlagValue()) {
        resolve(true);
      } else {
        reject(new Error("Timeout"));
      }
    });
  };

  private waitForNetwork = () => {
    return this.waitForFlag(() => IS_NET_CONNECTED, 60 * 10, 1000);
  };

  private createSocketAndAuthenticate = async () => {
    await this.waitForNetwork();
    if (this.isConnected()) return;
    this.socket = new WebSocket(this.getSocketUrl());
    await this.waitForFlag(this.isConnected, 5, 200);
    this.socket?.addEventListener("message", this.onMessage);
    await this.authenticate();
    this.socket?.addEventListener("close", this.reconnect);
  };

  private isConnected = () => {
    return !!this.socket && this.socket.readyState === WebSocket.OPEN;
  };

  private sendSocketMessage = async (message: object) => {
    return this.waitForResponseData((uuid) => {
      this.socket?.send(JSON.stringify({ ...message, uuid }));
    });
  };

  private sendToken = async () => {
    const token = sharedLib.storageService.getAccessToken();
    return this.sendSocketMessage({ token });
  };

  private authenticate = () => {
    return pRetry(
      () =>
        this.sendToken().then((response) => {
          const isSuccessfulLogin =
            response?.data.op === SocketOps.LOGIN && response?.code === 200;
          if (!isSuccessfulLogin) {
            if (response?.code !== 401) {
              throw new AbortError(
                `failed to authenticate with code: ${response?.code}`
              );
            }
            throw new Error(response?.message);
          }
        }),
      {
        minTimeout: 1000,
        retries: 1,
        onFailedAttempt: async (err) => {
          if (err.retriesLeft) {
            const tokenUpdated = await refreshAndPersistToken(true);
            if (!tokenUpdated) {
              throw new AbortError("failed to refresh token");
            }
          } else {
            throw new Error("all attempts to authenticate failed");
          }
        },
      }
    );
  };

  private handleWaitingResponse = (
    payload: ApiResponse<WebSocketResponse<unknown>>
  ) => {
    const uuid = payload.data.uuid;
    if (uuid) {
      this.pendingPromises[uuid]?.resolve(payload);
      delete this.pendingPromises[uuid];
    }
  };

  private onMessage = (event: any) => {
    const payload: ApiResponse<WebSocketResponse<unknown>> = JSON.parse(
      event.data
    );
    const socketOp = payload.data.op;
    this.handleWaitingResponse(payload);
    if (
      ![SocketOps.LOGIN, SocketOps.PING, SocketOps.UNSUBSCRIBE].includes(
        socketOp
      ) &&
      payload.code !== 401
    ) {
      const { topic, data } = payload.data;
      const { dispatch } = sharedLib.store;
      if (typeof payload.data === "object" && "data" in payload.data) {
        dispatch(socketsSlices[topic].actions.updateSocketData(data));
      }
    }
  };

  private resubscribeToTopic = async <Topic extends SocketTopics>(
    topic: Topic
  ) => {
    return this.sendUnsubscribeMsg(topic).finally(() => {
      return this.sendSubscribeMsg([topic]);
    });
  };

  private sendUnsubscribeMsg = async <Topic extends SocketTopics>(
    topic: Topic
  ) => {
    return this.sendSocketMessage({
      op: SocketOps.UNSUBSCRIBE,
      topics: [{ name: topic }],
    });
  };

  private getTopicSubscribeData = (topic: SocketTopics) => {
    const topicState = topicReducer[topic].getState();
    return { name: topic, tab_id: this.tabId, ...topicState };
  };

  private sendSubscribeMsg = async (topics: SocketTopics[]) => {
    return this.sendSocketMessage({
      op: SocketOps.SUBSCRIBE,
      topics: topics.map((topic) => this.getTopicSubscribeData(topic)),
    });
  };

  private resubscribeAllTopics = async () => {
    if (!this.isConnected()) return;
    this.subscriptionsMutex
      .runExclusive(() => {
        if (this.subscribedTopics.size) {
          return this.sendSubscribeMsg([...this.subscribedTopics]);
        }
      })
      .catch(() => {});
  };

  clear = (reconnect?: boolean) => {
    this.initialized = false;
    this.subscriptionsMutex
      .runExclusive(async () => {
        this.pendingSubscriptionsActions = {};
        this.subscribedTopics = new Set();
      })
      .catch(() => {});
    if (this.socket) {
      this.dispose();
    }
    if (reconnect) {
      this.connectionMutex
        .runExclusive(async () => {
          await this.connect();
        })
        .catch(() => {})
        .finally(() => {
          this.initialized = true;
        });
    }
  };

  dispose = () => {
    this.socket?.removeEventListener("message", this.onMessage);
    this.socket?.removeEventListener("close", this.reconnect);
    this.socket?.close();
    this.socket = null;
    this.pendingPromises = {};
  };

  private updateSubscriptionActions = (
    topic: SocketTopics,
    action: SubscriptionAction
  ) => {
    const previousData = this.pendingSubscriptionsActions[topic];
    if (
      previousData?.actionType === SubscriptionAction.SUBSCRIBE &&
      action === SubscriptionAction.RE_SUBSCRIBE
    ) {
      return;
    }
    this.pendingSubscriptionsActions[topic] = {
      actionType: action,
      numRetries: 0,
    };
  };

  reconnect = async () => {
    this.dispose();
    try {
      await this.connectionMutex.runExclusive(async () => {
        await this.connect();
        await this.resubscribeAllTopics();
      });
    } catch {}
  };

  private connect = async () => {
    try {
      await pRetry(this.createSocketAndAuthenticate, {
        onFailedAttempt: () => {
          this.dispose();
        },
        factor: 2,
        minTimeout: 1000,
        maxTimeout: 10000,
        retries: 10,
      });
    } catch {}
    return Promise.resolve(this.isConnected());
  };

  unSubscribe<Topic extends SocketTopics>(
    topic: Topic,
    args: SocketArgs<Topic>
  ) {
    this.subscriptionsMutex
      .runExclusive(() => {
        let handler = topicReducer[
          topic
        ] as unknown as TopicReducerHandler<Topic>;
        const [resubscribe, unsubscribe] = handler?.remove(args);
        let action: SubscriptionAction | undefined;
        if (resubscribe) action = SubscriptionAction.RE_SUBSCRIBE;
        else if (unsubscribe) action = SubscriptionAction.UNSUBSCRIBE;
        if (action) {
          this.updateSubscriptionActions(topic, action);
        }
      })
      .catch(() => {});
  }

  subscribe<Topic extends SocketTopics>(topic: Topic, args: SocketArgs<Topic>) {
    this.subscriptionsMutex
      .runExclusive(() => {
        let handler = topicReducer[
          topic
        ] as unknown as TopicReducerHandler<Topic>;
        const resubscribe = handler.add(args);
        let action: SubscriptionAction | undefined;
        if (!this.subscribedTopics.has(topic)) {
          action = SubscriptionAction.SUBSCRIBE;
        } else if (resubscribe) {
          action = SubscriptionAction.RE_SUBSCRIBE;
        }
        if (action) {
          this.updateSubscriptionActions(topic, action);
        }
      })
      .catch(() => {});
  }

  onAppBackground() {
    this.initialized = false;
    if (this.socket) {
      this.dispose();
    }
  }

  onAppForeground() {
    this.reconnect().finally(() => {
      this.initialized = true;
    });
  }
}

export default SocketHandler;

type WaitingResponseData = Record<
  string,
  {
    requestTime: Moment;
    resolve: (value?: ApiResponse<WebSocketResponse<unknown>>) => void;
    reject: (value: unknown) => void;
  }
>;

enum SubscriptionAction {
  SUBSCRIBE = "subscribe",
  RE_SUBSCRIBE = "resubscribe",
  UNSUBSCRIBE = "unsubscribe",
}

interface SubscriptionData {
  actionType: SubscriptionAction;
  numRetries: number;
}
